/**
 * Schema DSL — `defineSchema({ users: table<User>('id').withIndex(...) })`.
 *
 * The DSL is pure and synchronous: it produces a frozen, structurally-typed
 * {@link Schema} that {@link createStore} consumes. No IndexedDB is touched
 * here, so schemas are safe to declare at module scope (SSR-safe).
 */

import type {
	AnyTableBuilder,
	AnyTableDefinition,
	DefinedSchema,
	IndexDefinition,
	RowKey,
	TableBuilder,
	TableDefinition,
	TableInput,
} from './types'

/**
 * Begin a table definition. `keyPath` is the in-line primary-key property on
 * `T` (IndexedDB stores rows by an in-object key path).
 *
 * @example
 * ```ts
 * interface User { id: string; email: string; age: number }
 * const users = table<User>('id')
 *   .withIndex('byEmail', 'email', { unique: true })
 *   .withIndex('byAge', 'age')
 * ```
 */
export function table<T>(keyPath: RowKey<T>): TableBuilder<T> {
	const indexes: IndexDefinition<T>[] = []

	const builder: TableBuilder<T> = {
		withIndex(name, indexKeyPath, opts) {
			if (indexes.some((i) => i.name === name)) {
				throw new Error(
					`web-offline-store: duplicate index "${name}" on table keyed by "${String(keyPath)}"`,
				)
			}
			indexes.push({
				name,
				keyPath: indexKeyPath,
				unique: opts?.unique ?? false,
				multiEntry: opts?.multiEntry ?? false,
			})
			return builder
		},
		build(): TableDefinition<T> {
			return Object.freeze({
				keyPath,
				indexes: Object.freeze(indexes.slice()),
			}) as TableDefinition<T>
		},
	}

	return builder
}

function isBuilder(v: TableInput): v is AnyTableBuilder {
	return typeof (v as AnyTableBuilder).build === 'function'
}

/**
 * Freeze a `{ tableName: table<T>(...) }` map into a versioned {@link Schema}.
 * `version` defaults to 1 and must be a positive integer (IndexedDB
 * requirement).
 *
 * Accepts a builder or definition for *any* row type with no assertion at the
 * call site, including a dynamic key — `defineSchema({ [name]: table<Row>('id') })`.
 * The returned {@link DefinedSchema} preserves each table's row type, so
 * `InferRow<typeof schema, 'users'>` is the exact row interface.
 *
 * @example
 * ```ts
 * const schema = defineSchema(
 *   {
 *     users: table<User>('id').withIndex('byEmail', 'email', { unique: true }),
 *     posts: table<Post>('id').withIndex('byAuthor', 'authorId'),
 *   },
 *   2,
 * )
 * ```
 */
export function defineSchema<M extends Record<string, TableInput>>(
	tables: M,
	version = 1,
): DefinedSchema<M> {
	if (!Number.isInteger(version) || version < 1) {
		throw new Error(
			`web-offline-store: schema version must be a positive integer, got ${String(version)}`,
		)
	}
	const out: Record<string, AnyTableDefinition> = {}
	for (const [name, input] of Object.entries(tables)) {
		out[name] = isBuilder(input) ? input.build() : input
	}
	return Object.freeze({
		version,
		tables: Object.freeze(out),
	}) as never
}
